// セクションの背景色（fill）の選好を localStorage に保存する。
// 図形（shape-preference）と同じ仕組みで、組み込みプリセットと利用者が追加した
// カスタム色（最大 SECTION_FILL_CUSTOM_COLOR_LIMIT 件）から選ぶ。
// セクションは常に塗りあり（「背景なし」は提供しない）ため、センチネル値は持たない。

import { SECTION_DEFAULT_FILL } from "@refinement-canvas/domain";
import { isValidHexColor, normalizeHexColor } from "./pen-preference";
import { addColorToList, removeColorFromList, updateColorInList } from "./custom-color-list";

const FILL_KEY = "refinement-canvas:section:fill";
const CUSTOM_COLORS_KEY = "refinement-canvas:section:custom-colors";

// 描画時に半透明化（16%）して塗るため、プリセットはやや濃い系統でも領域の主張が強くなりすぎない。
export const SECTION_FILL_PRESET_COLORS: readonly string[] = [
  SECTION_DEFAULT_FILL, // グレー（既定）
  "#4c9aff", // ブルー
  "#57d9a3", // グリーン
  "#ffc400", // イエロー
  "#ff8f73", // オレンジ
  "#998dd9", // パープル
];

/** ローカル保持できるセクション背景色のカスタム色の上限。 */
export const SECTION_FILL_CUSTOM_COLOR_LIMIT = 12;

/**
 * セクションの背景色の選好を読み込む。有効な hex のときだけ採用し、それ以外は既定色。
 */
export function loadSectionFill(): string {
  try {
    const value = window.localStorage.getItem(FILL_KEY);
    if (isValidHexColor(value)) return normalizeHexColor(value);
    return SECTION_DEFAULT_FILL;
  } catch {
    return SECTION_DEFAULT_FILL;
  }
}

export function saveSectionFill(fill: string): void {
  try {
    if (!isValidHexColor(fill)) return;
    window.localStorage.setItem(FILL_KEY, normalizeHexColor(fill));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 追加保存されたセクション背景色のカスタム色一覧を読み込む。壊れた値は読み飛ばす。 */
export function loadSectionCustomColors(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: string[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= SECTION_FILL_CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * セクション背景色のカスタム色を 1 件追加する（図形の addShapeCustomColor と同じ規則）。
 * 返り値は更新後のカスタム色一覧。
 */
export function addSectionCustomColor(color: string): readonly string[] {
  const current = loadSectionCustomColors();
  const next = addColorToList(
    current,
    color,
    SECTION_FILL_PRESET_COLORS,
    SECTION_FILL_CUSTOM_COLOR_LIMIT
  );
  if (next === current) return current;
  try {
    window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

export function updateSectionCustomColor(
  previous: string,
  next: string
): { readonly colors: readonly string[]; readonly applied: boolean } {
  const current = loadSectionCustomColors();
  const result = updateColorInList(current, previous, next, SECTION_FILL_PRESET_COLORS);
  if (result.applied) {
    try {
      window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(result.colors));
    } catch {
      // ignore
    }
  }
  return result;
}

export function removeSectionCustomColor(color: string): readonly string[] {
  const current = loadSectionCustomColors();
  const next = removeColorFromList(current, color);
  if (next !== current) {
    try {
      window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }
  return next;
}
